import * as React from "react";

import { VariantProps, cva } from "class-variance-authority";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { Link as RouterLink } from "react-router-dom";
import { Slot } from "@radix-ui/react-slot";
import Tooltip from "@verse_core/shared_components/base/Tooltip";
import { cn } from "@verse_core/shared_components/ClassName";

const buttonVariants = cva(
  "tw-border-0 focus-visible:tw-ring-1 focus-visible:tw-outline-none hover:tw-no-underline hover:tw-transition-none",
  {
    variants: {
      colorScheme: {
        primary: "tw-text-blue-1 hover:tw-text-blue-2",
        secondary: "tw-text-gray-2 hover:tw-text-gray-1",
        warning: "tw-text-orange-1 hover:tw-text-orange-2",
        critical: "tw-text-red-1 hover:tw-text-red-2",
        success: "tw-text-green-1 hover:tw-text-green-2",
        accent: "tw-text-verse-text-primary hover:tw-text-verse-text-primary",
        // Rebrand buttons (login and sign-up cards).
        tide: "tw-font-sohne tw-text-verse-text-inverse hover:tw-text-verse-text-inverse",
        neutral:
          "tw-font-sohne tw-text-verse-text-secondary hover:tw-text-verse-text-secondary",
      },
      size: {
        sm: "tw-text-[12px] tw-font-medium",
        md: "tw-text-[14px] tw-font-medium",
        lg: "tw-text-[16px] tw-font-medium",
      },
      styleType: {
        solid:
          "tw-inline-flex tw-items-center tw-justify-center tw-whitespace-nowrap focus-visible:tw-outline-none focus-visible:tw-ring-1 hover:tw-cursor-pointer  disabled:tw-pointer-events-none",
        outline:
          "tw-inline-flex tw-items-center tw-justify-center tw-whitespace-nowrap focus-visible:tw-outline-none focus-visible:tw-ring-1  hover:tw-cursor-pointer disabled:tw-pointer-events-none tw-border tw-border-solid tw-border-[1px] tw-shadow-none",
        ghost:
          "tw-inline-flex tw-items-center tw-justify-center tw-whitespace-nowrap focus-visible:tw-outline-none focus-visible:tw-ring-1  hover:tw-cursor-pointer disabled:tw-pointer-events-none tw-bg-transparent",
        light:
          "tw-inline-flex tw-items-center tw-justify-center tw-whitespace-nowrap focus-visible:tw-outline-none focus-visible:tw-ring-1  hover:tw-cursor-pointer disabled:tw-pointer-events-none",
        textOnly:
          "tw-border-0 tw-bg-transparent hover:tw-bg-transparent disabled:tw-pointer-events-none disabled:tw-bg-transparent hover:tw-underline tw-m-0 tw-p-0 !tw-inline",
        textOnlyUnderlined:
          "tw-border-0 tw-bg-transparent tw-underline hover:tw-bg-transparent disabled:tw-pointer-events-none disabled:tw-bg-transparent tw-m-0 tw-p-0 !tw-inline",
      },
    },
    compoundVariants: [
      // Add shared sizing for "button" variants
      {
        styleType: ["solid", "outline", "ghost", "light"],
        size: "sm",
        className:
          "tw-h-[28px] tw-px-[8px] tw-py-[8px] tw-gap-[4px] tw-rounded-[8px]",
      },
      {
        styleType: ["solid", "outline", "ghost", "light"],
        size: "md",
        className:
          "tw-h-[36px] tw-px-[16px] tw-py-[10px] tw-gap-[8px] tw-rounded-[8px]",
      },
      {
        styleType: ["solid", "outline", "ghost", "light"],
        size: "lg",
        className:
          "tw-h-[48px] tw-px-[16px] tw-py-[12px] tw-gap-[8px] tw-rounded-[16px] hover:tw-shadow-[0_4px_12px_0_rgba(10,58,90,0.1)]",
      },
      // Outline colors
      {
        styleType: ["outline"],
        colorScheme: "secondary",
        className:
          "tw-bg-white tw-text-gray-2 hover:tw-bg-gray-light-4  disabled:tw-bg-white disabled:tw-text-gray-light-1 tw-border-gray-5 disabled:tw-border-gray-light-2",
      },
      {
        styleType: ["outline"],
        colorScheme: "primary",
        className:
          "tw-bg-white tw-text-blue-1  tw-border-blue-1 hover:tw-bg-blue-light-2  disabled:tw-bg-white disabled:tw-text-blue-light-2 disabled:tw-border-blue-light-2",
      },
      {
        styleType: ["outline"],
        colorScheme: "warning",
        className:
          "tw-bg-orange-light-1 tw-text-orange-1 tw-border-orange-1 hover:tw-bg-orange-light-0 hover:tw-text-orange-1  disabled:tw-bg-orange-light-1 disabled:tw-text-orange-2 disabled:tw-border-orange-2",
      },
      {
        styleType: ["outline"],
        colorScheme: "critical",
        className:
          "tw-bg-red-light-1 tw-text-red-1 tw-border-red-1 hover:tw-bg-red-light-2 hover:tw-text-red-1  disabled:tw-bg-red-light-1 disabled:tw-text-red-light-2 disabled:tw-border-red-light-2",
      },
      {
        styleType: ["outline"],
        colorScheme: "success",
        className:
          "tw-bg-green-light-1 tw-text-green-1 tw-border-green-1 hover:tw-bg-green-light-2 hover:tw-text-green-1  disabled:tw-bg-green-light-1 disabled:tw-text-green-light-2 disabled:tw-border-green-light-2",
      },
      // Light colors
      {
        styleType: ["light"],
        colorScheme: "primary",
        className:
          "tw-bg-blue-light-1 tw-text-blue-1 hover:tw-bg-blue-light-2  disabled:tw-bg-blue-light-1 disabled:tw-text-blue-light-2 disabled:tw-bg-blue-light-1",
      },
      {
        styleType: ["light"],
        colorScheme: "secondary",
        className:
          "tw-bg-gray-light-1 tw-text-gray-1  tw-border-gray-1 hover:tw-bg-gray-light-0 hover:tw-text-gray-1 disabled:tw-bg-gray-light-1 disabled:tw-text-gray-light-1 disabled:tw-bg-gray-light-3",
      },
      {
        styleType: ["light"],
        colorScheme: "warning",
        className:
          "tw-bg-orange-light-1 tw-text-orange-1 tw-border-orange-1 hover:tw-bg-orange-light-0 hover:tw-text-orange-1 disabled:tw-bg-orange-light-1 disabled:tw-text-orange-2 disabled:tw-bg-orange-light-1",
      },
      {
        styleType: ["light"],
        colorScheme: "critical",
        className:
          "tw-bg-red-light-1 tw-text-red-1 hover:tw-bg-red-light-2 hover:tw-text-red-1 disabled:tw-bg-red-light-1 disabled:tw-text-red-light-2 disabled:tw-bg-red-light-1",
      },
      {
        styleType: ["light"],
        colorScheme: "success",
        className:
          "tw-bg-green-light-1 tw-text-green-1  tw-border-green-1 hover:tw-bg-green-light-2  disabled:tw-bg-green-light-1 disabled:tw-text-green-light-2 disabled:tw-bg-green-light-1",
      },
      // Ghost Styles

      {
        styleType: "ghost",
        colorScheme: "primary",
        className:
          "tw-text-blue-1 hover:tw-text-blue-1 disabled:tw-text-blue-light-2 hover:tw-bg-blue-light-1",
      },
      {
        styleType: "ghost",
        colorScheme: "secondary",
        className:
          "tw-text-gray-2 hover:tw-text-gray-2 disabled:tw-text-gray-light-1 hover:tw-bg-gray-light-4",
      },
      {
        styleType: "ghost",
        colorScheme: "warning",
        className:
          "tw-text-orange-1 hover:tw-text-orange-1  hover:tw-bg-orange-light-1 disabled:tw-text-orange-2",
      },
      {
        styleType: "ghost",
        colorScheme: "critical",
        className:
          "tw-text-red-1 hover:tw-text-red-1 hover:tw-bg-red-light-1 disabled:tw-text-red-light-2",
      },
      {
        styleType: "ghost",
        colorScheme: "success",
        className:
          "tw-text-green-1 hover:tw-text-green-1 hover:tw-bg-green-light-1 disabled:tw-text-green-light-2",
      },

      // Text colors
      {
        styleType: ["textOnly", "textOnlyUnderlined"],
        colorScheme: "primary",
        className:
          "tw-text-blue-1 hover:tw-text-blue-2 disabled:tw-text-blue-light-2",
      },
      {
        styleType: ["textOnly", "textOnlyUnderlined"],
        colorScheme: "warning",
        className:
          "tw-text-orange-1 hover:tw-text-orange-2 disabled:tw-text-orange-2",
      },
      {
        styleType: ["textOnly", "textOnlyUnderlined"],
        colorScheme: "secondary",
        className:
          "tw-text-gray-1 hover:tw-text-gray-2 disabled:tw-text-gray-light-1",
      },
      {
        styleType: ["textOnly", "textOnlyUnderlined"],
        colorScheme: "critical",
        className:
          "tw-text-red-1 hover:tw-text-red-2 disabled:tw-text-red-light-2",
      },
      {
        styleType: ["textOnly", "textOnlyUnderlined"],
        colorScheme: "success",
        className:
          "tw-text-green-1 hover:tw-text-green-2 disabled:tw-text-green-light-2",
      },
      {
        styleType: "solid",
        colorScheme: "primary",
        className:
          "tw-bg-blue-2 tw-text-white hover:tw-bg-blue-0 hover:tw-text-white disabled:tw-pointer-events-none disabled:tw-bg-blue-light-2 disabled:tw-text-white",
      },
      {
        styleType: "solid",
        colorScheme: "secondary",
        className:
          "tw-bg-gray-0 tw-text-white hover:tw-bg-gray-2 hover:tw-text-white disabled:tw-pointer-events-none disabled:tw-bg-gray-light-0 disabled:tw-text-gray-light-1",
      },
      {
        styleType: "solid",
        colorScheme: "warning",
        className:
          "tw-bg-orange-1 tw-text-white hover:tw-bg-orange-0 hover:tw-text-white disabled:tw-pointer-events-none disabled:tw-bg-orange-2 disabled:tw-text-white",
      },
      {
        styleType: "solid",
        colorScheme: "critical",
        className:
          "tw-bg-red-1 tw-text-white hover:tw-bg-red-2 hover:tw-text-white disabled:tw-pointer-events-none disabled:tw-bg-red-light-2 disabled:tw-text-white",
      },
      {
        styleType: "solid",
        colorScheme: "success",
        className:
          "tw-bg-green-1 tw-text-white hover:tw-bg-green-2 hover:tw-text-white disabled:tw-pointer-events-none disabled:tw-bg-green-light-2 disabled:tw-text-white",
      },
      {
        styleType: "solid",
        colorScheme: "accent",
        className:
          "tw-bg-verse-button-accent tw-text-verse-text-primary hover:tw-bg-verse-button-accent hover:tw-text-verse-text-primary disabled:tw-pointer-events-none disabled:tw-bg-verse-sky disabled:tw-text-verse-text-disabled",
      },
      {
        styleType: "solid",
        colorScheme: "tide",
        className:
          "tw-bg-verse-tide hover:tw-bg-verse-button-tide-hovered disabled:tw-bg-verse-button-tide-disabled",
      },
      {
        styleType: "solid",
        colorScheme: "neutral",
        className:
          "tw-bg-verse-button-neutral hover:tw-bg-verse-button-neutral",
      },
      {
        styleType: "outline",
        colorScheme: "neutral",
        className:
          "tw-border-verse-border-secondary tw-bg-white hover:tw-bg-verse-surface-secondary",
      },
      // Rebrand sizing, after the shared sizing so these win.
      {
        colorScheme: ["tide", "neutral"],
        size: "sm",
        className: "tw-text-[14px] tw-font-normal",
      },
      {
        colorScheme: ["tide", "neutral"],
        size: "md",
        className: "tw-px-[10px] tw-font-normal",
      },
      {
        colorScheme: ["tide", "neutral"],
        size: "lg",
        className: "tw-rounded-[8px] tw-font-semibold hover:tw-shadow-none",
      },
    ],
    defaultVariants: {
      colorScheme: "primary",
      styleType: "solid",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  leftIcon?: IconProp;
  rightIcon?: IconProp;
  tooltip?: React.ReactNode;
  dataTestId?: string;
}

export interface LinkProps
  extends React.AnchorHTMLAttributes<HTMLAnchorElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  leftIcon?: IconProp;
  rightIcon?: IconProp;
  tooltip?: React.ReactNode;
  dataTestId?: string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      colorScheme,
      styleType,
      size,
      asChild = false,
      leftIcon,
      rightIcon,
      children,
      tooltip,
      dataTestId,
      ...props
    },
    ref,
  ) => {
    const baseType = "button";
    const Comp = asChild ? Slot : baseType;

    const isTextOnly =
      styleType === "textOnly" || styleType === "textOnlyUnderlined";
    const leftIconRequiredPadding = isTextOnly ? "tw-mr-[10px]" : "";
    const rightIconRequiredPadding = isTextOnly ? "tw-ml-[10px]" : "";

    const innerChildren = (
      <>
        {leftIcon && (
          <FontAwesomeIcon
            icon={leftIcon}
            className={leftIconRequiredPadding}
          />
        )}
        {children}
        {rightIcon && (
          <FontAwesomeIcon
            icon={rightIcon}
            className={rightIconRequiredPadding}
          />
        )}
      </>
    );

    return (
      <span className="tw-group">
        {tooltip && (
          <span className="tw-hidden group-hover:tw-block">
            <Tooltip text={tooltip} />
          </span>
        )}
        <Comp
          data-testid={dataTestId}
          className={cn(
            buttonVariants({ colorScheme, styleType, size }),
            className,
          )}
          ref={ref}
          {...props}
        >
          {innerChildren}
        </Comp>
      </span>
    );
  },
);
Button.displayName = "Button";

const Link = React.forwardRef<
  HTMLAnchorElement,
  LinkProps & { disabled?: boolean; tooltip?: React.ReactNode }
>(
  (
    {
      className,
      colorScheme,
      styleType,
      size,
      asChild = false,
      leftIcon,
      rightIcon,
      disabled,
      children,
      tooltip,
      dataTestId,
      href,
      ...props
    },
    ref,
  ) => {
    // Determine if this is an external link
    const isExternalLink =
      href?.startsWith("http://") || href?.startsWith("https://");
    const baseType = isExternalLink ? "a" : RouterLink;
    // modifiedProps below carries `href` for "a" and `to` for RouterLink.
    const Comp: React.ElementType = asChild ? Slot : baseType;

    const { leftIconRequiredMargin, rightIconRequiredMargin } = getIconMargin(
      styleType,
      size,
    );

    const innerChildren = (
      <>
        {leftIcon && (
          <FontAwesomeIcon icon={leftIcon} className={leftIconRequiredMargin} />
        )}
        {children}
        {rightIcon && (
          <FontAwesomeIcon
            icon={rightIcon}
            className={rightIconRequiredMargin}
          />
        )}
      </>
    );

    // Handle disabled state for links
    const handleClick = (e: React.MouseEvent<HTMLElement>) => {
      if (disabled) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      // Call original onClick if provided
      if (props.onClick) {
        props.onClick(e as React.MouseEvent<HTMLAnchorElement>);
      }
    };

    // Prepare props based on link type
    const modifiedProps = isExternalLink
      ? { ...props, href: disabled ? undefined : href, onClick: handleClick }
      : { ...props, to: disabled ? undefined : href, onClick: handleClick };

    return (
      <span className="tw-group">
        {tooltip && (
          <span className="tw-hidden group-hover:tw-block">
            <Tooltip text={tooltip} />
          </span>
        )}
        <Comp
          ref={ref}
          {...modifiedProps}
          className={cn(
            buttonVariants({ colorScheme, styleType, size }),
            disabled && getDisabledStyles(colorScheme, styleType),
            className,
          )}
          aria-disabled={disabled}
          tabIndex={disabled ? -1 : undefined}
          data-disabled={disabled}
          data-testid={dataTestId}
        >
          {innerChildren}
        </Comp>
      </span>
    );
  },
);
Link.displayName = "Link";

export { Button, Link, buttonVariants };

function getDisabledStyles(
  colorScheme: string | null | undefined,
  styleType: string | null | undefined,
): string {
  const baseDisabled = "tw-pointer-events-none";

  if (styleType === "solid") {
    switch (colorScheme) {
      case "primary":
        return `${baseDisabled} tw-bg-blue-light-2 tw-text-white`;
      case "secondary":
        return `${baseDisabled} tw-bg-gray-light-0 tw-text-gray-light-1`;
      case "warning":
        return `${baseDisabled} tw-bg-orange-2 tw-text-white`;
      case "critical":
        return `${baseDisabled} tw-bg-red-light-2 tw-text-white`;
      case "success":
        return `${baseDisabled} tw-bg-green-light-2 tw-text-white`;
      case "accent":
        return `${baseDisabled} tw-bg-verse-sky tw-text-verse-text-disabled`;
      default:
        return `${baseDisabled} tw-bg-blue-light-2 tw-text-white`;
    }
  }

  if (styleType === "outline") {
    switch (colorScheme) {
      case "secondary":
        return `${baseDisabled} tw-bg-white tw-text-gray-light-1 tw-border-gray-light-2`;
      case "primary":
        return `${baseDisabled} tw-bg-white tw-text-blue-light-2 tw-border-blue-light-2`;
      case "warning":
        return `${baseDisabled} tw-bg-orange-light-1 tw-text-orange-2 tw-border-orange-2`;
      case "critical":
        return `${baseDisabled} tw-bg-red-light-1 tw-text-red-light-2 tw-border-red-light-2`;
      case "success":
        return `${baseDisabled} tw-bg-green-light-1 tw-text-green-light-2 tw-border-green-light-2`;
      default:
        return `${baseDisabled} tw-bg-white tw-text-blue-light-2 tw-border-blue-light-2`;
    }
  }

  if (styleType === "light") {
    switch (colorScheme) {
      case "primary":
        return `${baseDisabled} tw-bg-blue-light-1 tw-text-blue-light-2`;
      case "secondary":
        return `${baseDisabled} tw-bg-gray-light-3 tw-text-gray-light-1`;
      case "warning":
        return `${baseDisabled} tw-bg-orange-light-1 tw-text-orange-2`;
      case "critical":
        return `${baseDisabled} tw-bg-red-light-1 tw-text-red-light-2`;
      case "success":
        return `${baseDisabled} tw-bg-green-light-1 tw-text-green-light-2`;
      default:
        return `${baseDisabled} tw-bg-blue-light-1 tw-text-blue-light-2`;
    }
  }

  if (styleType === "ghost") {
    switch (colorScheme) {
      case "primary":
        return `${baseDisabled} tw-text-blue-light-2`;
      case "secondary":
        return `${baseDisabled} tw-text-gray-light-1`;
      case "warning":
        return `${baseDisabled} tw-text-orange-2`;
      case "critical":
        return `${baseDisabled} tw-text-red-light-2`;
      case "success":
        return `${baseDisabled} tw-text-green-light-2`;
      default:
        return `${baseDisabled} tw-text-blue-light-2`;
    }
  }

  if (styleType === "textOnly" || styleType === "textOnlyUnderlined") {
    switch (colorScheme) {
      case "primary":
        return `${baseDisabled} tw-text-blue-light-2`;
      case "secondary":
        return `${baseDisabled} tw-text-gray-light-1`;
      case "warning":
        return `${baseDisabled} tw-text-orange-2`;
      case "critical":
        return `${baseDisabled} tw-text-red-light-2`;
      case "success":
        return `${baseDisabled} tw-text-green-light-2`;
      default:
        return `${baseDisabled} tw-text-blue-light-2`;
    }
  }

  return baseDisabled;
}

function getIconMargin(
  styleType: string | null | undefined,
  size: string | null | undefined,
) {
  const isTextOnly =
    styleType === "textOnly" || styleType === "textOnlyUnderlined";

  if (!isTextOnly) {
    return { leftIconRequiredMargin: "", rightIconRequiredMargin: "" };
  }

  switch (size) {
    case "sm":
      return {
        leftIconRequiredMargin: "tw-mr-[4px]",
        rightIconRequiredMargin: "tw-ml-[4px]",
      };
    case "md":
      return {
        leftIconRequiredMargin: "tw-mr-[8px]",
        rightIconRequiredMargin: "tw-ml-[8px]",
      };
    case "lg":
      return {
        leftIconRequiredMargin: "tw-mr-[8px]",
        rightIconRequiredMargin: "tw-ml-[8px]",
      };
    default:
      return {
        leftIconRequiredMargin: "tw-mr-[8px]",
        rightIconRequiredMargin: "tw-ml-[8px]",
      };
  }
}
